// ============================================================================
// LE PANNEAU ANCRÉ — géométrie et placement, partagés par le popover, le menu et le picker.
//
// ⚠️ EXTRAIT, PAS RÉÉCRIT. `anchorPanel` vivait dans le panneau de colonnes du tableau, où elle avait
// déjà réglé les deux vrais problèmes : la BASCULE au-dessus quand la place manque dessous, et le
// bornage à la fenêtre. Les autres panneaux du dépôt (cloche, recherche transverse, recherche SIRENE)
// ont chacun réinventé un placement — l'un d'eux n'a aucune sortie clavier, un autre est rogné par son
// conteneur défilant. On généralise celle qui marche au lieu d'en garder quatre.
//
// ⚠️ ANCRAGE MESURÉ + `position: fixed`, jamais `absolute` : un panneau en flux est rogné par le
// premier ancêtre en `overflow` (cas mesuré : panneau de 317 px dans un corps de 300 px, bouton
// « Créer » jamais atteignable) — et les panneaux de ce produit vivent précisément dans des conteneurs
// défilants bornés (tableau, modale).
// ============================================================================
import { Z_LAYERS } from './z';

/** Le rectangle du déclencheur, en coordonnées de fenêtre. */
export interface AnchorRect { left: number; right: number; top: number; bottom: number }
export interface ViewportSize { width: number; height: number }

/**
 * La géométrie demandée par l'appelant.
 * ⚠️ `width: 'trigger'` = au moins aussi large que le déclencheur (un picker sous son champ), borné par
 * `maxWidth` : sans le plafond, un champ pleine largeur produirait un panneau de suggestions aussi
 * large que l'écran.
 */
export interface PanelGeom {
  width: number | 'trigger';
  maxWidth?: number;
  offsetY: number;
  edgeGap: number;
  minHeight: number;
  maxHeight: number;
  viewportPad: number;
  /** Bord d'alignement : `end` colle le panneau à la DROITE du déclencheur (cloche, menus de fin de barre). */
  align?: 'start' | 'end';
}

export interface PanelPlacement {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  /** Où le panneau a RÉELLEMENT été posé — l'animation d'entrée en dépend (il doit sortir du déclencheur). */
  placement: 'below' | 'above';
}

/**
 * Position du panneau, BORNÉE à la fenêtre.
 *
 * ⚠️ `minHeight` est un SEUIL DE DÉCISION, pas un plancher de rendu. Appliqué en `Math.max`, il
 * annulait le plafonnement : un déclencheur en bas d'écran produisait un panneau débordant de la
 * fenêtre, pied compris — donc son bouton de validation inatteignable, exactement ce que le plafond
 * devait empêcher. Le panneau ne dépasse plus jamais : il change de côté, ou il rétrécit.
 */
export function anchorPanel(trigger: AnchorRect, viewport: ViewportSize, geom: PanelGeom): PanelPlacement {
  const largeurDemandee = geom.width === 'trigger' ? Math.max(0, trigger.right - trigger.left) : geom.width;
  const cap = Math.min(geom.maxWidth ?? Number.POSITIVE_INFINITY, Math.max(0, viewport.width - geom.edgeGap * 2));
  const width = Math.min(largeurDemandee, cap);

  // `end` aligne la DROITE du panneau sur celle du déclencheur ; `start` sa gauche sur la gauche.
  const souhaite = geom.align === 'start' ? trigger.left : trigger.right - width;
  const left = Math.max(geom.edgeGap, Math.min(souhaite, viewport.width - width - geom.edgeGap));

  const dessous = viewport.height - (trigger.bottom + geom.offsetY) - geom.viewportPad;
  const dessus = trigger.top - geom.offsetY - geom.viewportPad;

  // Dessous par défaut ; au-dessus seulement si la place y est vraiment meilleure.
  if (dessous >= geom.minHeight || dessous >= dessus) {
    return {
      left, width, top: trigger.bottom + geom.offsetY,
      maxHeight: Math.min(geom.maxHeight, Math.max(0, dessous)),
      placement: 'below',
    };
  }
  const maxHeight = Math.min(geom.maxHeight, Math.max(0, dessus));
  return {
    left, width,
    top: Math.max(geom.viewportPad, trigger.top - geom.offsetY - maxHeight),
    maxHeight,
    placement: 'above',
  };
}

/**
 * Les valeurs par défaut d'un panneau ancré.
 * ⚠️ Rayon `sm` (11) : ni 12 ni 13, qui n'appartiennent à aucun cran de la charte et ne se
 * distinguaient de rien. Ombre = le rôle `popover` : le picker empruntait l'alpha du rôle `modal`,
 * ce qui posait deux objets du même plan à deux profondeurs différentes — un accident, pas une
 * hiérarchie.
 */
export const POPOVER = {
  radius: 'sm',
  shadow: 'popover',
  enterMs: 150,
  offsetY: 6,
  edgeGap: 8,
  viewportPad: 14,
  maxWidth: 420,
  zScrim: Z_LAYERS.popoverScrim,
  zPanel: Z_LAYERS.popover,
  /** Liste d'options : 32 px est le cran `--h-xs` de la charte (une rangée n'est pas un bouton). */
  list: { rowHeight: 32, rowFontSize: 13, maxHeight: 216 },
  /**
   * Champ de recherche interne — TOUJOURS RENDU.
   * ⚠️ SEUIL SUPPRIMÉ (mesure 2026-08-16) : il valait 8, au motif qu'« un champ de recherche pour trois
   * valeurs est du bruit ». La maquette dit l'inverse et l'a écrit noir sur blanc chez elle — elle
   * affichait le champ au-delà de 8, « ce qui faisait passer les pickers courts pour des selects
   * natifs », et l'a corrigé en le rendant inconditionnel. Le seuil coûtait plus cher que ça : la
   * recherche SERVEUR n'est joignable que par ce champ, donc sous le seuil, une valeur hors de la
   * liste d'ouverture n'était plus atteignable du tout.
   */
  search: { height: 32, pad: 8 },
} as const;

/** Géométrie par défaut, prête à l'emploi. Un appelant n'en surcharge que ce qui le distingue. */
export const POPOVER_GEOM: PanelGeom = {
  width: 'trigger',
  maxWidth: POPOVER.maxWidth,
  offsetY: POPOVER.offsetY,
  edgeGap: POPOVER.edgeGap,
  minHeight: 200,
  maxHeight: 540,
  viewportPad: POPOVER.viewportPad,
  align: 'end',
};
